import style from "./style.module.scss";
import UKToast from "@/components/UKToast";
import { useTranslation } from "@/i18n/client";
import LoadingIcon from "@/components/loadingIcon";
import { CancelButton, IntlDialog, SubmitButton } from "@/components/intlMui";
import React, { useRef, useEffect, forwardRef, useState, useImperativeHandle } from "react";
import { Radio, Popover, RadioGroup, Typography, FormControl, FormControlLabel } from "@mui/material";
import { fetchUserReviewDetail, getReportReason, handleDeleteComment, reportComment } from "@/apis/service";
import { sendClickTrack as reviewImageVideoSendClickTrack, EXPOSURE_RECORD as REVIEW_IMAGE_VIDEO_EXPOSURE_RECORD } from "@/tracks/25333";

interface PropsType {
  nickName: string;
  content: string;
  currentIndex: number;
  totalIndex: number;
  reportCommentParams?: any;
  closeHandler?: () => void;
  reviewFooterCountyFn?: (type: string, params: any) => void;
}

// 举报弹框
const FormContent = forwardRef<HTMLDivElement>((props, ref): any => {
  FormContent.displayName = "FormContent";
  const { t } = useTranslation();
  const [reasonList, setReasonList] = useState<any[]>([]);
  const [formFieldValue, setFormFieldValue] = useState({
    reportReason: { value: "1" },
    reportContent: { value: "" },
  });

  const onTextareaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length >= 200) {
      return;
    }
    setFormFieldValue({
      ...formFieldValue,
      reportContent: { value: (event.target as HTMLInputElement).value },
    });
  };
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormFieldValue({
      ...formFieldValue,
      reportReason: { value: (event.target as HTMLInputElement).value },
    });
  };

  const getReportReasonList = async () => {
    const { reasonList: resReasonList = [] } = (await getReportReason()) || {};
    setReasonList(resReasonList);
  };
  useImperativeHandle(
    ref,
    () => {
      return {
        getFormFieldValue: () => {
          return formFieldValue;
        },
      } as any;
    },
    [formFieldValue],
  );

  useEffect(() => {
    getReportReasonList();
  }, []);

  return (
    <div className={style.reportFormWrapper}>
      <FormControl>
        <label id="demo-radio-buttons-group-label" className={style.reasonLabel}>
          {t("dp.reporting.reportingReason")}
        </label>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          value={formFieldValue["reportReason"].value}
          name="reportReason"
          onChange={handleRadioChange}
          sx={{
            "& .MuiRadio-root": {
              color: "rgba(224, 225, 229, 1)",
            },
          }}
        >
          {reasonList?.map((reason, index) => {
            return <FormControlLabel key={index} value={reason.id} control={<Radio />} label={reason.title} />;
          })}
        </RadioGroup>
      </FormControl>
      <div className={style.commentTextarea}>
        <p className={style.title}>{t("dp.reporting.detailDesc")}</p>
        <textarea
          value={formFieldValue["reportContent"].value}
          onChange={(e: any) => onTextareaChange(e)}
          placeholder={t("dp.reporting.detailDescPlaceholder")}
        ></textarea>
        <span className={style.num}>{formFieldValue.reportContent.value.length + 1}/200</span>
      </div>
    </div>
  );
});

const BottomReviewContent: React.FC<PropsType> = ({ nickName, content, currentIndex, totalIndex, reportCommentParams, closeHandler, reviewFooterCountyFn }) => {
  const { t } = useTranslation();
  const formRef = useRef<any>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    reviewImageVideoSendClickTrack.Evaluate_Picture_More({ commentId: reportCommentParams?.commentId });
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);
  const id = popoverOpen ? "simple-popover" : "";

  // 举报弹框打开
  const onReportAndBlock = () => {
    setOpenDialog(true);
    handlePopoverClose();
  };

  // 举报弹框关闭
  const reportAndBlockClose = () => {
    setOpenDialog(false);
  };

  // 提交举报
  const onFormSubmit = async () => {
    // 如果评论内容为空，且没有图片和视频，则不支持举报
    if (!content && totalIndex === 0) {
      UKToast.show({
        content: t("previewResource.alert"),
        icon: "warning",
      });
      return;
    }
    const r: any = await fetchUserReviewDetail({
      skuId: reportCommentParams?.skuId,
      parentReplyId: reportCommentParams?.parentReplyId || 0,
      commentId: reportCommentParams?.commentId,
    });
    if (!r?.success) {
      UKToast.show({
        content: r?.msg,
        icon: "warning",
      });
      return;
    }
    const formData = formRef.current.getFormFieldValue();
    const formParams = Object.keys(formData).reduce((pre, cur) => {
      return { ...pre, [cur]: formData[cur].value };
    }, {});
    const data = {
      commentId: r?.data?.commentId,
      targetReplyId: r?.data?.targetReplyId,
      toPin: r?.data?.userPin,
      toContent: r?.data?.content,
    };
    const res = await reportComment({
      ...formParams,
      ...(data || {}),
    });
    if (res?.data?.saveResult) {
      setOpenDialog(false);
      UKToast.show({
        sx: { zIndex: 10002 },
        icon: "success",
        content: (
          <>
            <p>{t("previewResource.reportSuccess")}</p>
            <p>{t("previewResource.reportSuccessThankYou")}</p>
          </>
        ),
      });
      reportAndBlockClose();
    } else if (res.code !== "") {
      UKToast.show({
        sx: { zIndex: 10002 },
        icon: "warning",
        content: res.msg || (
          <>
            <p>{t("profile.unsubscribe.unsuccess.title")}</p>
            <p>{t("profile.unsubscribe.unsuccess.desc")}</p>
          </>
        ),
      });
    } else {
      UKToast.show({
        sx: { zIndex: 10002 },
        content: res?.msg || t("previewResource.reportFailed"),
        icon: "error",
      });
    }
  };

  // 删除评价
  const onDeleteReview = async () => {
    setIsLoading(true);
    try {
      const res: any = await handleDeleteComment({
        skuId: reportCommentParams?.skuId as string,
        commentId: reportCommentParams?.commentId as string,
      });
      if (res && res.success) {
        UKToast.show({
          icon: "success",
          content: t("common.message.deleteSuccessful"),
        });
        reviewFooterCountyFn?.("previewDelete", {
          skuId: reportCommentParams?.skuId,
          commentId: reportCommentParams?.commentId,
        });
        setDeleteOpen(false);
        setIsLoading(false);
        closeHandler?.();
      } else {
        UKToast.show({
          icon: "error",
          content: res.msg || t("common.message.deleteFailed"),
        });
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={style.header}>
        <div className={style.nickName}>{nickName}</div>
        <div className={style.currentIndex}>
          <span>
            {currentIndex + 1}/{totalIndex}
          </span>
          <i
            style={{ padding: "0 10px", cursor: "pointer" }}
            aria-describedby={id}
            onClick={(e: any) => handleClick(e)}
            exptag={"exp|" + REVIEW_IMAGE_VIDEO_EXPOSURE_RECORD.Evaluate_Picture_More_Expo}
            data-exptag-json-param={JSON.stringify({ commentId: reportCommentParams?.commentId })}
            data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          >
            ···
          </i>
          <Popover
            id={id}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            sx={{
              zIndex: 9999,
              "& .MuiPopover-paper": {
                boxShadow: "0px 4px 28px 0px rgba(0, 0, 0, 0.08)",
              },
            }}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            {reportCommentParams?.currentUserOperation && (
              <Typography
                sx={{ padding: "10px 20px" }}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setDeleteOpen(true);
                  handlePopoverClose();
                  /** 点击事件上报-更多浮层项点击 */
                  reviewImageVideoSendClickTrack.Evaluate_Picture_More_Layer({ commentId: reportCommentParams?.commentId, title: "1" });
                }}
                exptag={"exp|" + REVIEW_IMAGE_VIDEO_EXPOSURE_RECORD.Evaluate_Picture_More_Layer_Expo}
                data-exptag-json-param={JSON.stringify({ commentId: reportCommentParams?.commentId, title: "1" })}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
              >
                {t("common.btnText.delete")}
              </Typography>
            )}
            {!reportCommentParams?.currentUserOperation && (
              <Typography
                sx={{ p: 1 }}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  onReportAndBlock();
                  handlePopoverClose();
                  /** 点击事件上报-更多浮层项点击 */
                  reviewImageVideoSendClickTrack.Evaluate_Picture_More_Layer({ commentId: reportCommentParams?.commentId, title: "4" });
                }}
                exptag={"exp|" + REVIEW_IMAGE_VIDEO_EXPOSURE_RECORD.Evaluate_Picture_More_Layer_Expo}
                data-exptag-json-param={JSON.stringify({ commentId: reportCommentParams?.commentId, title: "4" })}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
              >
                {t("dp.commentSection.report")}
              </Typography>
            )}
          </Popover>
        </div>
      </div>
      <div className={style.content}>{content}</div>
      {/* 举报弹框 */}
      <IntlDialog
        width={520}
        sx={{ zIndex: 10002 }}
        title={t("dp.reporting.title")}
        aria-labelledby="customized-dialog-title"
        open={openDialog}
        showButtonAction={false}
        onClose={reportAndBlockClose}
        content={
          <>
            <div className={style.reportReasonWrapper}>{t("dp.reporting.placeholder")}</div>
            <FormContent ref={formRef} />
            <div className={style.dialogFooterAction}>
              <CancelButton onClick={reportAndBlockClose}>{t("common.btnText.cancel")}</CancelButton>
              <SubmitButton onClick={onFormSubmit}>{t("common.btnText.submit")}</SubmitButton>
            </div>
          </>
        }
      />
      {/* 删除弹窗 */}
      <IntlDialog
        width={520}
        sx={{ zIndex: 12002 }}
        open={deleteOpen}
        showButtonAction={false}
        onClose={() => setDeleteOpen(false)}
        content={
          <>
            <div className={style.deleteText}>{t("review.deleteConfirm")}</div>
            <div className={style.dialogFooterAction}>
              <CancelButton onClick={() => setDeleteOpen(false)}>{t("common.btnText.cancel")}</CancelButton>
              <SubmitButton disabled={isLoading} startIcon={isLoading ? <LoadingIcon /> : null} onClick={onDeleteReview}>
                {t("common.btnText.submit")}
              </SubmitButton>
            </div>
          </>
        }
      />
    </>
  );
};

export default BottomReviewContent;
